import React, { useState, useEffect, useCallback } from "react";
import { Minus, Plus, Trash } from "lucide-react";
import {
  CONTENT_GENERATION,
  getContentGeneration,
  isStaticContentStep,
  supportsStaticContent,
} from "@/features/campaigns/utils/stepContentMode";
import CommonDropdownMenu from "@/shared/components/ui/DropDown/DropdownMenu";
// import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { FixedMessageModal, SwitchToAiModal } from "./Form/StepContentModals";
import ToggleField from "@/shared/components/ui/Form/ToggleField";
import TextButton from "@/shared/components/ui/Button/TextButton";
import { useGetCampaignById, useUpdateCampaignStep } from "@/features/campaigns/hooks/useCampaign";
import { useCampaignId } from "@/features/campaigns/hooks/useCampaignId";
import { getActionLabel, getChannelIcon } from "@/shared/utils/helperFunctions";
import { Button } from "@heroui/react";
import CustomSelect from "@/shared/components/ui/Select/CustomSelect";
// import { StatusChip } from "@/shared/components/ui/Status/StatusChip";
import { messageStyleOptions } from "@/shared/utils/aiConfig";

const ConditionNode = ({ data, onEdit: _onEdit, onDelete }) => {
  const getInitialDelay = useCallback(() => {
    if (data?.duration && typeof data.duration === "object") {
      const delay = {
        days: data.duration.days || 0,
        hours: data.duration.hours || 0,
        minutes: data.duration.minutes || 0,
      };
      // If all values are 0, set default to 1 day
      if (delay.days === 0 && delay.hours === 0 && delay.minutes === 0) {
        return { days: 1, hours: 0, minutes: 0 };
      }
      return delay;
    }
    return { days: 1, hours: 0, minutes: 0 };
  }, [data?.duration]);

  // Determine initial selected unit
  const getInitialUnit = (delay) => {
    if (delay.days > 0) return "days";
    if (delay.hours > 0) return "hours";
    if (delay.minutes > 0) return "minutes";
    return "days";
  };

  const initialDelay = getInitialDelay();
  const [delay, setDelay] = useState(initialDelay);
  const [selectedUnit, setSelectedUnit] = useState(
    getInitialUnit(initialDelay),
  );
  // "fixed" = write/edit the fixed message, "ai" = confirm switching back to AI.
  const [contentModal, setContentModal] = useState(null);
  const canUseFixedMessage = supportsStaticContent(data);
  const isFixedMessage = isStaticContentStep(data);
  const campaignId = useCampaignId();
  const { data: campaignDetails } = useGetCampaignById(campaignId, Boolean(campaignId));
  // With AI off campaign-wide, every message step must use a fixed message.
  const campaignAiOff =
    getContentGeneration(campaignDetails?.data ?? campaignDetails) === CONTENT_GENERATION.OFF;
  const { mutate: updateStep } = useUpdateCampaignStep();
  const selected = false;

  const stepNumber = data?.step_order || 1;
  const stepTitle = getActionLabel(data) || "Initial Outreach";
  const channel = data?.channel || "email";
  const messageStyle = data?.message_style || "";
  const selectedMessageStyle = messageStyleOptions.find(
    (item) => item.key?.trim() === messageStyle?.trim(),
  )?.title;

  const actions = [
    {
      id: "delete",
      name: "Delete",
      onClick: (itemData) => {
        onDelete?.({ item: itemData, variant: "step" });
      },
      icon: <Trash size={16} color="#ef4444" />,
    },
  ];

  // Update delay when value changes
  const updateDelay = (newDelay) => {
    setDelay(newDelay);
    if (!campaignId || !data?.id) return;

    updateStep({
      campaignId,
      stepId: data.id,
      stepData: {
        ...data,
        conditions: data?.conditions ? JSON.stringify(data?.conditions) : null,
        duration: newDelay,
      },
    });
  };

  // Update delay when data changes
  useEffect(() => {
    const newDelay = getInitialDelay();
    setDelay(newDelay);
    setSelectedUnit(getInitialUnit(newDelay));
  }, [getInitialDelay]);

  const handleDecrease = () => {
    const currentValue = delay[selectedUnit] || 0;
    if (currentValue > 1) {
      const newDelay = {
        days: 0,
        hours: 0,
        minutes: 0,
      };
      newDelay[selectedUnit] = currentValue - 1;
      updateDelay(newDelay);
    }
  };

  const handleIncrease = () => {
    const currentValue = delay[selectedUnit] || 0;
    const newDelay = {
      days: 0,
      hours: 0,
      minutes: 0,
    };
    newDelay[selectedUnit] = currentValue + 1;
    updateDelay(newDelay);
  };

  const handleUnitChange = (selectedKeys) => {
    const newUnit = Array.from(selectedKeys)[0];
    if (newUnit && newUnit !== selectedUnit) {
      setSelectedUnit(newUnit);
      const newDelay = {
        days: 0,
        hours: 0,
        minutes: 0,
      };
      newDelay[newUnit] = 1;
      updateDelay(newDelay);
    }
  };

  const unitOptions = [
    { key: "days", title: "Day(s)" },
    { key: "hours", title: "Hour(s)" },
    { key: "minutes", title: "Minute(s)" },
  ];

  const currentValue = delay[selectedUnit] || 0;
  const [valueInput, setValueInput] = useState(String(currentValue));

  useEffect(() => {
    setValueInput(String(currentValue));
  }, [currentValue, selectedUnit]);

  const commitDelayValue = useCallback(() => {
    const parsed = parseInt(valueInput, 10);
    const nextValue = Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
    const newDelay = {
      days: 0,
      hours: 0,
      minutes: 0,
    };
    newDelay[selectedUnit] = nextValue;
    setValueInput(String(nextValue));
    updateDelay(newDelay);
  }, [selectedUnit, updateDelay, valueInput]);

  return (
    <React.Fragment>
      <div
        className={`p-5 rounded-2xl border shadow-none w-[435px] bg-white dark:bg-gray-900 flex flex-col gap-5 ${
          selected ? "border-secondary" : "border-gray-200 dark:border-gray-800"
        }`}
      >
        {/* step and condition details */}
        <div className="flex items-center justify-between w-full gap-2">
          <div className="px-2 flex items-center">
            <p className="text-[0.815rem] font-inter text-gray-800 dark:text-gray-100 font-semibold">
              Step {stepNumber}
            </p>
            <div className="w-px h-4 bg-gray-200 dark:bg-gray-700 ml-2" />
            <span className="mx-2">
              {getChannelIcon({ channel, isCircular: true })}
            </span>
            <p className="text-[0.815rem] font-inter text-gray-800 dark:text-gray-100 font-bold">
              {stepTitle}
            </p>
          </div>
          <CommonDropdownMenu actions={actions} itemId={data} />
        </div>
        {canUseFixedMessage && (
          <ToggleField
            label="AI-personalized"
            description={
              isFixedMessage
                ? "Off: every lead gets this fixed message with their details filled in."
                : campaignAiOff
                  ? "AI is off for this campaign. Write a fixed message for this step."
                  : "AI writes this step for each lead."
            }
            checked={!isFixedMessage && !campaignAiOff}
            disabled={campaignAiOff}
            onChange={(checked) => setContentModal(checked ? "ai" : "fixed")}
            action={
              isFixedMessage || campaignAiOff ? (
                <TextButton onClick={() => setContentModal("fixed")}>
                  {isFixedMessage ? "Edit message" : "Write message"}
                </TextButton>
              ) : null
            }
          />
        )}
        {/* setting actions */}

        {/* <div className="flex flex-col gap-1.5">
              {messageStyle && (
                <StatusChip
                  label={`Message Style : ${selectedMessageStyle}`}
                  variant="neutral"
                  className={"w-fit"}
                />
              )}
              <SecondaryButton
                title={"Advance Settings"}
                icon={
                  <Settings
                    size={16}
                    className="text-gray-700"
                    strokeWidth={1.4}
                  />
                }
                onClick={handleAdvanceSettingsModalOpen}
                className="text-[0.715rem] font-normal text-gray-700 w-fit  font-inter shadow-none"
              />
            </div> */}
        {stepNumber > 1 && (
          <div className="flex justify-end  w-full gap-2">
            <div className="flex items-center gap-1 p-2 border-b border-gray-300 dark:border-gray-700">
              <Button
                className={`p-1 bg-gray-100 dark:bg-gray-800 rounded-full w-5 h-5 min-w-0 ${
                  currentValue <= 1 ? "opacity-50" : ""
                }`}
                onPress={handleDecrease}
                disabled={currentValue <= 1}
              >
                <Minus size={14} className="text-gray-800 dark:text-gray-200" />
              </Button>
              <input
                type="text"
                inputMode="numeric"
                aria-label="Delay amount"
                value={valueInput}
                onChange={(e) => {
                  setValueInput(e.target.value.replace(/\D/g, ""));
                }}
                onBlur={commitDelayValue}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.currentTarget.blur();
                  }
                }}
                className="w-8 min-w-[2ch] max-w-[5ch] border-0 bg-transparent p-0 text-center text-[0.7rem] font-inter font-bold text-gray-800 dark:text-gray-100 outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <Button
                className="p-1 bg-gray-100 dark:bg-gray-800 rounded-full w-5 h-5 min-w-0"
                onPress={handleIncrease}
              >
                <Plus size={14} className="text-gray-800 dark:text-gray-200" />
              </Button>
              <div className="ml-1">
                <CustomSelect
                  options={unitOptions}
                  selectedKeys={new Set([selectedUnit])}
                  onSelectionChange={handleUnitChange}
                  triggerClassName="!border-none !bg-transparent !shadow-none !w-[100px] !p-0 !m-0 !min-h-0 h-fit"
                  selectItemClassName="hover:!bg-gray-100 dark:hover:!bg-gray-800 !text-gray-800 dark:!text-gray-100"
                  label={null}
                  endContent={null}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      {contentModal === "fixed" && (
        <FixedMessageModal isOpen step={data} onClose={() => setContentModal(null)} />
      )}
      {contentModal === "ai" && (
        <SwitchToAiModal isOpen step={data} onClose={() => setContentModal(null)} />
      )}
    </React.Fragment>
  );
};

export default ConditionNode;
